import { Button, Form, notification, Spin } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { dataForRvdUsersTable, prepareDataForOvertimeLoading } from 'shared/helpers/utils';
import { DownloadOutlined, StopOutlined, UploadOutlined } from '@ant-design/icons';
import Breadcrumbs from 'shared/components/breadcrumbs/Breadcrumbs';
import CommonTable from 'features/tables/commonTable';
import { TCommonColumns } from 'shared/types/overtime.types';
import { useNavigate, useParams } from 'react-router-dom';
import { RvdStatuses, RvdStatusesBack } from 'shared/enums/rvd';
import {
  useCheckRvdUserMutation,
  useLazyGetRvdByIdQuery,
  useLazyGetRvdUsersQuery,
  useLoadRvdMutation,
} from 'app/services/edpApiV1/rvd';
import { downloadExcell } from 'shared/helpers/excell/excell';
import EmployeeInput from 'features/absence/madalAddOneAbsence/employeeInput';
import { TDivisionEmployees } from 'shared/types/types';
import { useLazyGetDivisionEmployeesQuery } from 'app/services/edpApiV1/viewer';
import { useLazyGetEmployeesQuery } from 'app/services/edpApiV1/employees';
import { useNotifications } from 'shared/hooks/use-notifications';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { useAppDispatch, useAppSelector } from 'app/store';
import { viewerModel } from 'entities/viewer';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import { uiModel } from 'entities/ui';
import { TabKeys } from 'features/divisions';
import { NewsTabs } from 'shared/enums/news';
import { TRvdResponseData } from 'shared/types/rvd.types';
import { RvdTabs } from '..';
import BadgeData from '../rvdArchive/bageData/BadgeData';
import style from './index.module.scss';

const FORM_ITEM_NAME = 'loadUpdate';

const LoadRvd = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [getRvdById, { data, isLoading, isFetching }] = useLazyGetRvdByIdQuery();

  const [getEmployees, { data: employees }] = useLazyGetEmployeesQuery();
  const currentOrganizationid = useAppSelector(viewerModel.me.selectors.getMeOrganizationId);
  const [isLoadAllowed, setIsLoadAllowed] = useState<boolean>(false);
  const [checkErrors, { isLoading: isCheckLoading }] = useCheckRvdUserMutation();
  const dispatch = useAppDispatch();

  const [getRvds, { data: rvdUserResponse, isFetching: isUserFetching, isLoading: isUserLoading }] =
    useLazyGetRvdUsersQuery();
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const [signatory, setSignatory] = useState<TDivisionEmployees | null>(null);
  const [getSignatories, { data: signatories }] = useLazyGetDivisionEmployeesQuery();

  const [loadRvdQuery, { isSuccess: isRvdLoaded, isLoading: isRvdLoading, isError: isRvdRejected, reset }] =
    useLoadRvdMutation();

  const navigate = useNavigate();

  const downloadLink = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    getRvdById({ id: id ? +id : 0 });
  }, []);

  useEffect(() => {
    getRvds({
      size: currentSize,
      page: currentPage,
      wowId: id ? +id : 0,
    });
  }, [currentSize, currentPage, getRvds, id]);

  useEffect(() => {
    if (data?.employees) {
      let isUserWrong = false;
      for (const user of data?.employees) {
        if (user.comment) {
          setIsLoadAllowed(false);
          isUserWrong = true;
        }
      }
      if (!isUserWrong) {
        setIsLoadAllowed(true);
      }
    }
    if (data?.comment) {
      setIsLoadAllowed(false);
    }
  }, [data?.employees]);

  useEffect(() => {
    if (employees?.data) {
      const [user] = employees.data;
      form.setFieldValue(FORM_ITEM_NAME, user.id);
      setSignatory({ ...user, division: user.division?.name || '', position: '' });
      getSignatories({
        page: 1,
        size: 10,
        search: `${user.lastName} ${user.name} ${user.middleName}`,
        divisionId: user.division?.divisionId,
        allAvailableOrganizations,
      });
    }
  }, [employees]);

  useEffect(() => {
    if (data) {
      getEmployees({
        isActive: true,
        page: 1,
        size: 1,
        personnelNumber: data.docSignatory,
        allAvailableOrganizations,
        organizationId: currentOrganizationid,
      });
    }
  }, [data, allAvailableOrganizations]);

  useNotifications(isRvdLoaded, isRvdRejected, 'Заявка выгружена в 1С', 'Заявка не выгружена в 1С', reset, 'topRight');

  const breadCrumbs = [
    { name: 'Работа в выходной день', link: '/rvd' },
    { name: 'Выгрузка в 1С', link: `/rvd/load/${id}` },
  ];

  const columns: TCommonColumns = [
    {
      title: 'Дата работ',
      dataIndex: 'toDate',
      key: 'toDate',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Табельный номер',
      dataIndex: 'personnelNumber',
      key: 'personnelNumber',
    },
    {
      title: 'Подразделение',
      dataIndex: 'division',
      key: 'division',
    },
    {
      title: 'Время работы',
      dataIndex: 'duration',
      key: 'duration',
    },
    {
      title: 'Способ компенсации',
      dataIndex: 'type',
      key: 'type',
    },
  ];

  const download = () => {
    downloadExcell(data);
  };

  const loadToCproduct = async () => {
    try {
      await loadRvdQuery({ wowId: data?.id || -1 }).unwrap();
      dispatch(uiModel.Tabs.actions.setActive(RvdTabs.my));
      navigate('/rvd');
    } catch (e: any) {
      console.error(e);
    }
  };

  const cancelLoad = () => {
    dispatch(uiModel.Tabs.actions.setActive(RvdTabs.my));
    navigate('/rvd');
  };

  const onRefreshError = async () => {
    if (!data?.employees) {
      return;
    }

    try {
      const users = data?.employees.map(elem => Number(elem.id));
      await checkErrors({ wowUsersIds: users });
      notification.success({ message: 'Обновлено!' });
      await getRvdById({ id: id ? +id : 0 });
      await getRvds({
        size: currentSize,
        page: currentPage,
        wowId: id ? +id : 0,
      });
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
  };

  return (
    <>
      <div className={style['top-side']}>
        <h3 className={style.title}>
          <Breadcrumbs breadCrumbs={breadCrumbs} />
        </h3>
        <div className={style.buttons}>
          <Button onClick={cancelLoad}>
            <StopOutlined /> Отмена
          </Button>
          <Button type="primary" onClick={download}>
            <DownloadOutlined /> Скачать
            <a href="#" ref={downloadLink} style={{ display: 'none' }}></a>
          </Button>
          {data?.status !== RvdStatuses.LOADED_CPRODUCT ? (
            <Button
              onClick={loadToCproduct}
              loading={isRvdLoading}
              disabled={!isLoadAllowed || data?.status !== RvdStatusesBack.NOT_LOADED_CPRODUCT}
            >
              <UploadOutlined /> Выгрузить в 1С
            </Button>
          ) : null}
        </div>
      </div>
      <div className={style.statusData}>
        {data?.docNumber && (
          <p>
            <span>№ документа в 1С:</span>
            <span>{data?.docNumber}</span>
          </p>
        )}
        <p>
          <span>Статус документа:</span>
          <span>
            <BadgeData rvd={data as TRvdResponseData} />
          </span>
        </p>
      </div>
      <Spin spinning={isFetching || isLoading || isUserFetching || isUserLoading}>
        <CommonTable
          dataSource={dataForRvdUsersTable(rvdUserResponse?.data ?? [])}
          columns={columns}
          currentPage={currentPage}
          currentSize={currentSize}
          setCurrentPage={setCurrentPage}
          setCurrentSize={setCurrentSize}
          total={rvdUserResponse?.pagination.total}
        />
      </Spin>
      <Form
        form={form}
        name="basic"
        layout="vertical"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        initialValues={{ remember: true }}
        onFinish={() => {}}
        autoComplete="off"
      >
        <EmployeeInput
          data={signatories}
          getEmployees={getSignatories}
          setEmployee={setSignatory}
          divisionName={signatory?.division}
          label="Руководитель"
          className="form-item-rvd"
          onChangeEmployee={() => {}}
          formItemName={FORM_ITEM_NAME}
          disabled={true}
        />
      </Form>
      {data?.userComment && (
        <div>
          <p>
            <span>Комментарий: </span>
            <span>{data?.userComment}</span>
          </p>
        </div>
      )}
      {(data?.comment || !isLoadAllowed) && (
        <div className={style.error}>
          <p className={style.nullMargin}>Ошибка: </p>
          <Button onClick={onRefreshError} loading={isCheckLoading}>
            Проверить заявку
          </Button>
        </div>
      )}
      {data?.comment ? <p>{data?.comment}</p> : null}
      {!isLoadAllowed &&
        data?.employees.map(user => {
          if (user.comment) {
            return <p>{user.comment}</p>;
          }
          return null;
        })}
    </>
  );
};

export default LoadRvd;
